/**
 * EmployeeAppSkeleton — non-interactive visual placeholder for the employee app
 *
 * Rendered while:
 *  1. Lazy chunks for /user/* are downloading (Suspense fallback)
 *  2. AppContext is still resolving the current user (appIsReady === false)
 *  3. AccountContext is still resolving the active account (accountIsReady === false)
 *
 * It mimics the layout of `HomeView` (mobile header, three tabs, three card
 * placeholders) so the user sees something close to the real UI instead of a
 * full-screen spinner with "Loading account..." text.
 *
 * IMPORTANT — this component is intentionally:
 *  - 100% presentational: no `useApp`, no `useSparkplugAccount`, no data fetching.
 *    That way it can render safely before any context/queries are ready.
 *  - non-interactive: tabs/buttons do not respond to taps. Once the real
 *    `HomeView` mounts it takes over wholesale and the user gets the real UI.
 *  - dependency-light: uses inline SVG/divs rather than the full
 *    `AppHeaderMobile` / radix `Tabs` / `SparkStatusButtons` components so it
 *    pulls minimal extra code into whichever chunk renders it.
 *
 * Styling is done with Tailwind utility classes only (no SCSS) to stay
 * consistent with the rest of the employee app.
 *
 * See: docs/features/employee-app-startup-skeleton.md for the broader rationale.
 */
import { FC } from 'react';

import { CircularProgress as MuiCircularProgress } from '@mui/material';
import clsx from 'clsx';

import Skeleton from '@components/layout/Skeleton';

const TabPlaceholder: FC<{ label: string; active?: boolean; icon: JSX.Element }> = ({
    label,
    active = false,
    icon,
}) => (
    <div
        className={clsx(
            'flex-1 flex items-center justify-center gap-2 px-2 py-3 text-[15px] font-medium relative',
            active
                ? // Active tab: dark text + cerulean underline to match HomeView's
                  // `data-[state=active]:after:bg-cerulean-700` styling.
                  "text-gray-950 after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:rounded-md after:bg-cerulean-600"
                : 'text-gray-700',
        )}
    >
        <span className="inline-flex items-center [&_svg]:text-current">{icon}</span>
        {label}
    </div>
);

/**
 * Inline copies of the real `HomeView` tab icons so the skeleton can render
 * before the icon-library chunk loads.
 *
 * These paths are byte-for-byte copies of:
 *   - `componentLibrary/Icon/Spark.svg`    → SparkIcon
 *   - `componentLibrary/Icon/Dollar.svg`   → DollarIcon (the "Earn" tab)
 *   - `componentLibrary/Icon/Calendar.svg` → CalendarIcon
 *
 * If those SVGs ever change, update the paths here too. Using
 * `fill="currentColor"` means the icon colour follows the tab text
 * (cerulean-600 active, gray-700 otherwise) — same behaviour as HomeView.
 */
const SparkTabIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 18.6666C14.7865 18.6666 18.6667 14.7864 18.6667 9.99998C18.6667 5.21351 14.7865 1.33331 10 1.33331C5.21357 1.33331 1.33337 5.21351 1.33337 9.99998C1.33337 14.7864 5.21357 18.6666 10 18.6666ZM6.33337 11.1666L10.3718 3.66665V8.85896H13.8334L9.79491 16.359V11.1666H6.33337Z"
            fill="currentColor"
        />
    </svg>
);
const EarnTabIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <path
            d="M9.96499 1.3C5.1511 1.3 1.29999 5.15111 1.29999 9.965C1.29999 14.7789 5.1511 18.63 9.96499 18.63C14.7789 18.63 18.63 14.7789 18.63 9.965C18.63 5.15111 14.7789 1.3 9.96499 1.3ZM9.38582 8.42305C9.58138 8.61862 9.86721 8.90444 10.3486 9.19403C11.3114 9.58139 11.9883 9.965 12.3719 10.4464C12.8533 10.9278 13.0451 11.601 13.0451 12.4697C13.0451 13.2369 12.8533 13.9139 12.3719 14.3953C11.8905 14.8767 11.3114 15.1625 10.5442 15.2603V16.7044H9.4836V15.2603C8.71263 15.1625 8.03943 14.8767 7.55804 14.2975C7.07665 13.8161 6.78707 13.0451 6.78707 11.9883H8.71262C8.71262 12.5637 8.81041 13.0451 9.09999 13.3347C9.2918 13.6243 9.58138 13.7183 9.96499 13.7183C10.3486 13.7183 10.5442 13.6243 10.736 13.3347C10.9278 13.1429 11.0255 12.7556 11.0255 12.3719C11.0255 11.9883 10.9278 11.6988 10.736 11.4092C10.5442 11.2174 10.2546 10.9278 9.86721 10.736C8.90443 10.3486 8.23124 9.965 7.74985 9.48361C7.26846 9.00222 7.07665 8.32903 7.07665 7.46027C7.07665 6.69306 7.26846 6.01611 7.74985 5.53472C8.23124 5.05333 8.81041 4.76751 9.58138 4.66972V3.22556H10.6382V4.7675C11.4092 4.86153 11.9883 5.24889 12.3719 5.73028C12.7555 6.21167 13.0451 6.97889 13.0451 7.84764H11.0255C11.0255 7.36625 10.9278 6.88486 10.736 6.59528C10.5442 6.30569 10.3486 6.21167 9.96499 6.21167C9.6754 6.21167 9.38582 6.30569 9.2918 6.59528C9.19401 6.78708 9.09999 7.17445 9.09999 7.55806C9.19401 7.94166 9.2918 8.23125 9.38582 8.42305Z"
            fill="currentColor"
        />
    </svg>
);
const EventsTabIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <path
            d="M4.66667 1.09998V2.87775H3.77778C2.8 2.87775 2 3.67775 2 4.65553V17.1C2 18.0778 2.8 18.8778 3.77778 18.8778H16.2222C17.2 18.8778 18 18.0778 18 17.1V4.65553C18 3.67775 17.2 2.87775 16.2222 2.87775H15.3333V1.09998H13.5556V2.87775H6.44444V1.09998H4.66667ZM3.77778 7.3222H16.2222V17.1H3.77778V7.3222ZM10.8889 11.7666V15.3222H14.4444V11.7666H10.8889Z"
            fill="currentColor"
        />
    </svg>
);

interface EmployeeAppSkeletonProps {
    /**
     * Optional label to display alongside the floating spinner. Defaults to
     * nothing — the visual skeleton is usually communicative enough on its own
     * and a label tends to make the screen feel busier.
     */
    label?: string;
}

const EmployeeAppSkeleton: FC<EmployeeAppSkeletonProps> = ({ label }) => {
    return (
        <div
            className="relative flex flex-col min-h-full bg-white"
            aria-busy="true"
            aria-live="polite"
        >
            {/* Header — mirrors AppHeaderMobile: ~56px tall, 16px padding, soft
                bottom shadow. Title placeholder on the left, notification-bell
                placeholder on the right. */}
            <div className="flex items-center justify-between px-4 h-14 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                <Skeleton width={180} height={28} containerClassName="inline-block" />
                <Skeleton circle width={32} height={32} />
            </div>

            {/* Sticky tab bar — same 3 tabs as HomeView (Sparks / Earn / Events),
                non-interactive. `top-14` so it sits flush under the header. */}
            <div className="flex w-full px-4 bg-gray-25 border-b border-gray-100 sticky top-14 z-10">
                <TabPlaceholder label="Sparks" active icon={<SparkTabIcon />} />
                <TabPlaceholder label="Earn" icon={<EarnTabIcon />} />
                <TabPlaceholder label="Events" icon={<EventsTabIcon />} />
            </div>

            {/* Status buttons placeholder (Active / Upcoming / Complete row). */}
            <div className="px-4 pt-4 pb-3">
                <Skeleton width="100%" height={36} containerClassName="block" />
            </div>

            {/* Spark card placeholders — match the 3-card grid in `SparksModule`
                while loading. */}
            <div className="flex flex-col gap-3 px-4 pb-4">
                <Skeleton height={177} />
                <Skeleton height={177} />
                <Skeleton height={177} />
            </div>

            {/* Subtle centred spinner. Wrapped in a translucent white pill so it
                remains readable on top of the card placeholders. */}
            <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 px-4 py-2 bg-white/70 rounded-full">
                <MuiCircularProgress size={24} thickness={4} />
                {label ? <span className="text-xs text-gray-700">{label}</span> : null}
            </div>
        </div>
    );
};

export default EmployeeAppSkeleton;
